import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import kebabCase from 'lodash-es/kebabCase';

import { HomeFacade } from '@home/home.facade';
import { isNullOrUndefined } from '@app/util/util';
import {
  HeaderWidget, HeaderWidgetConfig, HeaderWidgetType
} from '@design-system/components/templates/header/entities/header.types';
import { AnimatedHeaderState, DsHeaderConfig, HeaderPageType, HeaderProductType } from '@header/entities/ds-header-config';
import { HomeItemDetailFacade } from '@home-item-detail/home-item-detail.facade';
import { HotelDetailFacade } from '@hotels/hotel-detail/hotel-detail.facade';

@Component({
  selector: 'app-header-animated',
  templateUrl: './header-animated.container.html',
  styleUrls: ['./header-animated.container.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HeaderAnimatedContainer {

  @Input() dsHeaderConfig: DsHeaderConfig;
  @Input() widgets: HeaderWidgetConfig[] = [];
  @Input() animatedHeaderState = AnimatedHeaderState.Init;
  @Input() isMobile: boolean;

  @Output() widgetClicked: EventEmitter<HeaderWidget> = new EventEmitter<HeaderWidget>();

  constructor(
    private readonly homeItemDetailFacade: HomeItemDetailFacade,
    private readonly hotelDetailFacade: HotelDetailFacade,
    private readonly homeFacade: HomeFacade
  ) { }

  get enabledAnimatedHeader$(): Observable<boolean> {
    return this.isLoadingViewOfAnimatedHeader$.pipe(
      map((isLoading) => this.isMobile && !isNullOrUndefined(this.dsHeaderConfig?.animatedType) && !isLoading)
    );
  }

  get widgets$(): Observable<HeaderWidgetConfig[]> {
    return this.dynamicTitle$.pipe(
      map(dynamicTitle => {
        return this.widgets?.map((widget) => {
          if (widget?.Type === HeaderWidgetType.Title) {
            return { ...widget, Label: dynamicTitle };
          } else {
            return widget;
          }
        });
      })
    );
  }

  public toKebabCase(value: string): string {
    return value ? kebabCase(value) : '';
  }

  private get dynamicTitle$(): Observable<string> {
    if (this.dsHeaderConfig?.pageType === HeaderPageType.ResultDetailPage) {
      return this.dsHeaderConfig?.productType === HeaderProductType.Hotels ?
        this.hotelDetailFacade.hotelDetail$.pipe(map(detail => detail?.Info?.HotelName ?? '')) : of('');
    } else if (this.dsHeaderConfig?.pageType === HeaderPageType.LandingPage) {
      return this.homeItemDetailFacade.homeItemDetail$.pipe(map(detail => detail?.title ?? ''));
    } else {
      return of('');
    }
  }

  private get isLoadingViewOfAnimatedHeader$(): Observable<boolean> {
    if (this.dsHeaderConfig?.pageType === HeaderPageType.ResultDetailPage) {
      return this.dsHeaderConfig?.productType === HeaderProductType.Hotels ?
        this.hotelDetailFacade.isLoadingHotelDetail$ : of(false);
    } else if (this.dsHeaderConfig?.pageType === HeaderPageType.HomePage) {
      return this.homeFacade.isLoadingHome$;
    } else if (this.dsHeaderConfig?.pageType === HeaderPageType.LandingPage) {
      return this.homeItemDetailFacade.isLoadingHomeItemDetail$;
    } else {
      return of(false);
    }
  }
}
